/**
 * Employee home screen — smartphone-like grid of clickable app tiles.
 * Replaces the widget-based dashboard for employee role.
 *
 * Each tile : square card, gradient color background, big white phosphor
 * icon, French label below, optional badge (e.g. unread chat count).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Timer, CalendarDots, Buildings, Warehouse, Wrench, QrCode,
  ClockCounterClockwise, Receipt, Car, Folder, AirplaneTilt, ChatCircle,
  NewspaperClipping,
} from '@phosphor-icons/react';

const TILES = [
  { to: '/time-entry',       icon: Timer,                label: 'Pointage',          grad: ['#1D4ED8', '#3B82F6'] },
  { to: '/my-planning',      icon: CalendarDots,         label: 'Mon Planning',      grad: ['#7C3AED', '#A855F7'] },
  { to: '/mes-chantiers',    icon: Buildings,            label: 'Mes chantiers',     grad: ['#EA580C', '#FB923C'] },
  { to: '/fil-actualite',    icon: NewspaperClipping,    label: 'Fil d\'actualité',  grad: ['#F59E0B', '#FCD34D'] },
  { to: '/stocks-materiaux', icon: Warehouse,            label: 'Stocks',            grad: ['#0891B2', '#22D3EE'] },
  { to: '/my-equipment',     icon: Wrench,               label: 'Mon matériel',      grad: ['#16A34A', '#4ADE80'] },
  { to: '/scan-materiel',    icon: QrCode,               label: 'Scan matériel',     grad: ['#0F766E', '#14B8A6'] },
  { to: '/my-hours',         icon: ClockCounterClockwise, label: 'Mes heures',       grad: ['#9333EA', '#C084FC'] },
  { to: '/finance',          icon: Receipt,              label: 'Notes de frais',    grad: ['#DC2626', '#F87171'] },
  { to: '/my-vehicle',       icon: Car,                  label: 'Mon véhicule',      grad: ['#0369A1', '#0EA5E9'] },
  { to: '/my-documents',     icon: Folder,               label: 'Mes documents',     grad: ['#CA8A04', '#FACC15'] },
  { to: '/employees',        icon: AirplaneTilt,         label: 'Congés / Absences', grad: ['#0D9488', '#2DD4BF'] },
  { to: '/chat',             icon: ChatCircle,           label: 'Chat',              grad: ['#BE185D', '#EC4899'] },
];

const EmployeeHomeScreen = ({ user }) => {
  const navigate = useNavigate();

  return (
    <div data-testid="employee-home-screen" style={{ padding: '12px 4px 32px' }}>
      {/* Greeting header — minimalist */}
      <div style={{ padding: '8px 12px 18px' }}>
        <div style={{ fontSize: '0.78rem', color: '#5E5F66', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Bonjour
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: '1.6rem', fontWeight: 700, color: '#0F172A' }}>
          {user?.name?.split(' ')[0] || 'Salarie'}
        </h1>
      </div>

      {/* Tile grid — 3 cols on mobile, 4-6 cols on larger screens */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: '14px',
          padding: '0 12px',
          maxWidth: 880,
          margin: '0 auto',
        }}
      >
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={`${t.to}-${t.label}`}
              type="button"
              data-testid={`home-tile-${t.label.toLowerCase().replace(/[^a-z]+/gi, '-').replace(/^-|-$/g, '')}`}
              onClick={() => navigate(t.to)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 6,
                padding: 0,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: '22px',
                  background: `linear-gradient(135deg, ${t.grad[0]}, ${t.grad[1]})`,
                  boxShadow: '0 6px 14px rgba(15, 23, 42, 0.14), inset 0 -2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  maxWidth: 130,
                  margin: '0 auto',
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.94)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.94)'; }}
                onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <Icon size={42} weight="duotone" />
              </span>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F172A', textAlign: 'center', lineHeight: 1.2, maxWidth: 130 }}>
                {t.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EmployeeHomeScreen;
